/// Diagrammatic illustrations of the four classical sextant errors
/// — used in the Reference tab as an explainer and in the "Spot the
/// sextant error" game with the kind hidden until reveal.
///
/// The conventions are the standard textbook ones:
///
/// - **none / side / index** — the navigator has the sextant set to 0°
///   and is looking at the horizon. The right half of the field of
///   view is the direct horizon (seen through the clear side of the
///   horizon glass); the left half is the reflected horizon (off the
///   index mirror).
/// - **perpendicularity** — the arc test: at ~35° on the arc, look at
///   the arc itself through the index mirror. Direct and reflected
///   arc should form one continuous line; a step shows the index
///   mirror is out of perpendicular with the frame.
/// - **collimation** — the same body (e.g. the Sun) is brought to
///   different parts of the field of view; if the telescope axis is
///   not parallel to the frame, the altitude reads differently
///   depending on where the body sits.

export type SextantErrorKind =
  | "none"
  | "perpendicularity"
  | "side"
  | "index"
  | "collimation";

interface Props {
  kind: SextantErrorKind;
  size?: number;
}

const VW = 220;
const VH = 180;
const CX = VW / 2;
const CY = VH / 2;
const R = 72;

const STROKE = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";
const ACCENT = "#c0382b";

function Eyepiece() {
  return (
    <>
      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="var(--bg-elev, #f8fafc)"
        stroke={STROKE}
        strokeWidth={2}
      />
      {/* Faint vertical bisector — split between direct (right) and
          reflected (left) views. */}
      <line
        x1={CX}
        y1={CY - R}
        x2={CX}
        y2={CY + R}
        stroke={MUTED}
        strokeWidth={0.6}
        strokeDasharray="3 3"
      />
    </>
  );
}

function HorizonView({ kind }: { kind: "none" | "side" | "index" }) {
  // Direct horizon (right half) — always straight across the centre.
  const directY = CY;
  const directLeft = CX;
  const directRight = CX + R - 4;

  // Reflected horizon (left half) — varies per error.
  const reflLeft = CX - R + 4;
  let reflPath: { x1: number; y1: number; x2: number; y2: number };
  if (kind === "none") {
    reflPath = { x1: reflLeft, y1: CY, x2: CX, y2: CY };
  } else if (kind === "index") {
    // Parallel but vertically offset — the classic "step" at 0°.
    reflPath = { x1: reflLeft, y1: CY - 12, x2: CX, y2: CY - 12 };
  } else {
    // Side error — reflected horizon tilted; meets the direct one at
    // the centre but at an angle.
    reflPath = { x1: reflLeft, y1: CY + 14, x2: CX, y2: CY };
  }

  return (
    <>
      <Eyepiece />
      {/* Direct horizon */}
      <line
        x1={directLeft}
        y1={directY}
        x2={directRight}
        y2={directY}
        stroke={STROKE}
        strokeWidth={2.5}
      />
      {/* Reflected horizon */}
      <line
        x1={reflPath.x1}
        y1={reflPath.y1}
        x2={reflPath.x2}
        y2={reflPath.y2}
        stroke={kind === "none" ? STROKE : ACCENT}
        strokeWidth={2.5}
      />
      {/* "Reflected" / "Direct" labels in subtle annotation. */}
      <text x={reflLeft - 2} y={CY + R + 14} fontSize={10} fill={MUTED}>
        reflected
      </text>
      <text
        x={directRight + 2}
        y={CY + R + 14}
        fontSize={10}
        textAnchor="end"
        fill={MUTED}
      >
        direct
      </text>
    </>
  );
}

function ArcView() {
  // Perpendicularity test — looking at the arc through the index
  // mirror. Direct view of the arc on one side, reflected on the
  // other. A kink shows the mirror is out of perpendicular.
  //
  // We draw the arc as a curved line entering from the left
  // (direct part of the sextant's arc) and exiting on the right
  // (the index mirror's reflection of the same arc). With the
  // mirror correctly perpendicular the two pieces form one
  // continuous curve; a perpendicularity error puts a visible
  // step where the reflection rejoins.
  return (
    <>
      <Eyepiece />
      {/* Direct portion of the arc — a gentle arc from lower-left
          rising to the centre. */}
      <path
        d={`M ${CX - 56} ${CY + 26} Q ${CX - 28} ${CY - 6} ${CX} ${CY - 8}`}
        fill="none"
        stroke={STROKE}
        strokeWidth={2.5}
      />
      {/* Reflected portion — should continue smoothly from the
          direct part, but starts ~12px above instead → visible
          step. */}
      <path
        d={`M ${CX} ${CY - 22} Q ${CX + 28} ${CY - 18} ${CX + 56} ${CY + 6}`}
        fill="none"
        stroke={ACCENT}
        strokeWidth={2.5}
      />
      {/* Tick marks on the arc — keep them aligned to whichever
          segment they belong to. */}
      {[-44, -22, 0].map((dx) => (
        <line
          key={`d${dx}`}
          x1={CX + dx}
          y1={CY + 22 + dx * 0.6}
          x2={CX + dx}
          y2={CY + 18 + dx * 0.6}
          stroke={MUTED}
          strokeWidth={1}
        />
      ))}
      {[0, 22, 44].map((dx) => (
        <line
          key={`r${dx}`}
          x1={CX + dx}
          y1={CY - 20 + dx * 0.45}
          x2={CX + dx}
          y2={CY - 16 + dx * 0.45}
          stroke={MUTED}
          strokeWidth={1}
        />
      ))}
      <text x={CX - 56} y={CY + R + 14} fontSize={10} fill={MUTED}>
        direct arc
      </text>
      <text
        x={CX + 56}
        y={CY + R + 14}
        fontSize={10}
        textAnchor="end"
        fill={MUTED}
      >
        reflected
      </text>
    </>
  );
}

function CollimationView() {
  // Collimation error — same body, two FOV positions, two different
  // readings. Draw the Sun disc near the top of the field and again
  // at the centre, with little reading annotations pointing to each.
  const sd = 8; // Sun-disc radius
  const top = { x: CX - 18, y: CY - 38 };
  const mid = { x: CX + 4, y: CY + 6 };

  return (
    <>
      <Eyepiece />
      {/* Horizon line through centre. */}
      <line
        x1={CX - R + 4}
        y1={CY + 24}
        x2={CX + R - 4}
        y2={CY + 24}
        stroke={STROKE}
        strokeWidth={2}
      />
      {/* Two Sun discs — same body, two FOV positions. */}
      <circle cx={top.x} cy={top.y} r={sd} fill="#f5b400" stroke={STROKE} strokeWidth={1} />
      <circle cx={mid.x} cy={mid.y} r={sd} fill="#f5b400" stroke={STROKE} strokeWidth={1} />
      {/* Reading callouts. */}
      <line
        x1={top.x + sd}
        y1={top.y}
        x2={CX + R + 8}
        y2={top.y - 12}
        stroke={ACCENT}
        strokeWidth={1}
      />
      <text
        x={CX + R + 10}
        y={top.y - 10}
        fontSize={10}
        fill={ACCENT}
      >
        reads 45° 14′
      </text>
      <line
        x1={mid.x + sd}
        y1={mid.y}
        x2={CX + R + 8}
        y2={mid.y + 12}
        stroke={ACCENT}
        strokeWidth={1}
      />
      <text
        x={CX + R + 10}
        y={mid.y + 14}
        fontSize={10}
        fill={ACCENT}
      >
        reads 45° 09′
      </text>
      <text
        x={CX - R}
        y={CY + R + 14}
        fontSize={10}
        fill={MUTED}
      >
        same body, two positions
      </text>
    </>
  );
}

export function SextantErrorFigure({ kind, size = VW }: Props) {
  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabelFor(kind)}
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      {kind === "perpendicularity" ? (
        <ArcView />
      ) : kind === "collimation" ? (
        <CollimationView />
      ) : (
        <HorizonView kind={kind} />
      )}
    </svg>
  );
}

function ariaLabelFor(kind: SextantErrorKind): string {
  switch (kind) {
    case "none":
      return "Eyepiece view: horizon and reflected horizon perfectly aligned";
    case "index":
      return "Eyepiece view: reflected horizon parallel but vertically offset";
    case "side":
      return "Eyepiece view: reflected horizon tilted relative to the direct horizon";
    case "perpendicularity":
      return "Arc test view: arc and its reflection don't form a continuous line";
    case "collimation":
      return "Same body in two parts of the field of view gives two readings";
  }
}

/// Human-readable label for the error kind, used in choice buttons
/// and reveal text.
export function sextantErrorLabel(kind: SextantErrorKind): string {
  switch (kind) {
    case "none":
      return "No error — adjustments good";
    case "perpendicularity":
      return "Perpendicularity error";
    case "side":
      return "Side error";
    case "index":
      return "Index error";
    case "collimation":
      return "Collimation error";
  }
}

/// Short cause/fix explainer, shown on reveal.
export function sextantErrorExplain(kind: SextantErrorKind): {
  cause: string;
  fix: string;
} {
  switch (kind) {
    case "none":
      return {
        cause:
          "All four adjustments are good — the reflected horizon falls exactly on the direct horizon at 0°.",
        fix:
          "Nothing to fix. Re-check before each session; mirrors drift with knocks and temperature.",
      };
    case "perpendicularity":
      return {
        cause:
          "The index mirror is not perpendicular to the plane of the sextant frame; the arc's reflection rejoins the direct arc with a visible step.",
        fix:
          "Set the arc to about 35°, look at the arc through the index mirror, and turn the screw behind the index mirror until the arc and its reflection form one continuous line.",
      };
    case "side":
      return {
        cause:
          "The horizon glass is not perpendicular to the frame; the reflected horizon meets the direct horizon at an angle rather than continuing it straight.",
        fix:
          "At 0°, view the horizon and adjust the screw on the back of the horizon glass that's further from the frame until the two horizons sit in one straight line.",
      };
    case "index":
      return {
        cause:
          "The index mirror and horizon glass aren't parallel at the 0° setting; the reflected horizon is parallel to the direct horizon but vertically offset.",
        fix:
          "Adjust the screw on the back of the horizon glass closer to the frame until the two horizons are level. Any residual offset is the index error — record it and apply it to every sight.",
      };
    case "collimation":
      return {
        cause:
          "The telescope axis is not parallel to the plane of the sextant frame; the altitude reads differently depending on where the body sits in the field of view.",
        fix:
          "Less critical for hand-held work; bring the body into the centre of the field of view for every sight. Most modern sextants have a fixed scope and no field adjustment.",
      };
  }
}
